import { a as softParse, c as getApiBaseUrl, d as setAuthTokenGetter, i as parseApiResponse, l as getAuthToken, n as logZodError, r as pageOf, s as withGgeGatewayPath, t as handleZodError, u as setApiBaseUrl } from "./validate.js";
import axios, { isAxiosError } from "axios";
import * as z from "zod";
//#region src/runtime/error.ts
const errorResponseSchema = z.object({
	errorCode: z.string(),
	description: z.string(),
	detail: z.string().optional(),
	appName: z.string().optional(),
	meta: z.unknown().optional()
});
/** Нормализованная ошибка, которую бросает мутатор. Сохраняет HTTP-статус и тело по схеме. */
var ApiError = class extends Error {
	status;
	response;
	constructor(message, status, response) {
		super(message);
		this.name = "ApiError";
		this.status = status;
		this.response = response;
	}
};
/** Приводит любую ошибку axios к `ApiError` с телом по схеме `ErrorResponse`, если оно подходит. */
const normalizeError = (error) => {
	if (isAxiosError(error)) {
		const status = error.response?.status;
		const parsed = errorResponseSchema.safeParse(error.response?.data);
		const body = parsed.success ? parsed.data : void 0;
		return new ApiError(body?.description ?? error.message, status, body);
	}
	if (error instanceof Error) return new ApiError(error.message);
	return new ApiError("Неизвестная ошибка запроса");
};
//#endregion
//#region src/runtime/mutator.ts
const serializeParams = (params) => {
	const search = new URLSearchParams();
	const append = (key, value) => {
		if (value === void 0 || value === null) return;
		if (Array.isArray(value)) {
			for (const item of value) append(key, item);
			return;
		}
		if (typeof value === "object") {
			for (const [nestedKey, nestedValue] of Object.entries(value)) append(nestedKey, nestedValue);
			return;
		}
		search.append(key, String(value));
	};
	for (const [key, value] of Object.entries(params)) append(key, value);
	return search.toString();
};
const AXIOS_INSTANCE = axios.create({ paramsSerializer: serializeParams });
AXIOS_INSTANCE.interceptors.request.use((config) => {
	config.baseURL ||= getApiBaseUrl();
	config.url = withGgeGatewayPath(config.url);
	const token = getAuthToken();
	if (token) config.headers.set("Authorization", `Bearer ${token}`);
	return config;
});
const customInstance = (config, options) => AXIOS_INSTANCE({
	...config,
	...options
}).then(({ data }) => data).catch((error) => {
	throw normalizeError(error);
});
//#endregion
//#region src/runtime/files-extra.ts
const uploadExternalRequestFile = (params, file, signal) => customInstance({
	url: "/api/mdm/external/files/api/request/upload",
	method: "POST",
	headers: { "Content-Type": "application/octet-stream" },
	params,
	data: file,
	signal
});
/**
* POST /api/mdm/files/api/request/upload/multi — мультизагрузка файлов.
* В спеке тело описано как `PartEvent[]` без схемы → Kubb генерит битый код (операция выкинута).
* Принимаем готовый `FormData` с полями бэкенда и шлём как multipart.
*/
const multiUploadExternalFiles = (formData, signal) => customInstance({
	url: "/api/mdm/external/files/api/request/upload/multi",
	method: "POST",
	headers: { "Content-Type": "multipart/form-data" },
	data: formData,
	signal
});
const uploadInternalRequestFile = (params, file, signal) => customInstance({
	url: "/api/mdm/internal/files/api/request/upload",
	method: "POST",
	headers: { "Content-Type": "application/octet-stream" },
	params,
	data: file,
	signal
});
/**
* POST /api/mdm/files/api/request/upload/multi — мультизагрузка файлов.
* В спеке тело описано как `PartEvent[]` без схемы → Kubb генерит битый код (операция выкинута).
* Принимаем готовый `FormData` с полями бэкенда и шлём как multipart.
*/
const multiUploadInternalFiles = (formData, signal) => customInstance({
	url: "/api/mdm/internal/files/api/request/upload/multi",
	method: "POST",
	headers: { "Content-Type": "multipart/form-data" },
	data: formData,
	signal
});
const importInternalRequestArchive = (file, signal) => {
	const formData = new FormData();
	formData.append("file", file);
	return customInstance({
		url: "/api/internal/requests/import",
		method: "POST",
		headers: { "Content-Type": "multipart/form-data" },
		data: formData,
		signal
	});
};
const exportExternalOperatorRequestsZip = (ids, signal) => customInstance({
	url: "/api/mdm/external/api/external/operator/requests/export",
	method: "POST",
	headers: { "Content-Type": "application/json" },
	data: { ids },
	responseType: "blob",
	signal
});
const exportInternalRequestsZip = (ids, signal) => customInstance({
	url: "/api/mdm/internal/api/internal/requests/export",
	method: "POST",
	headers: { "Content-Type": "application/json" },
	data: { ids },
	responseType: "blob",
	signal
});
const exportExternalOperatorRequestZip = (id, signal) => exportExternalOperatorRequestsZip([id], signal);
const exportInternalResultZip = (id, signal) => exportInternalRequestsZip([id], signal);
const downloadExternalRequestFile = (id, signal) => customInstance({
	url: "/api/mdm/external/files/api/request/download",
	method: "POST",
	headers: { "Content-Type": "application/json" },
	data: { id },
	responseType: "blob",
	signal
});
/** Опубликованные документы итогового решения для заявителя (external contour). */
const downloadExternalFinalDecisionFile = (params, signal) => customInstance({
	url: "/api/mdm/external/files/api/final-decision/download",
	method: "POST",
	headers: { "Content-Type": "application/json" },
	data: params,
	responseType: "blob",
	signal
});
const downloadInternalRequestFile = (id, signal) => customInstance({
	url: "/api/mdm/internal/files/api/request/download",
	method: "POST",
	headers: { "Content-Type": "application/json" },
	data: { id },
	responseType: "blob",
	signal
});
const uploadSupportingDocumentFile = (params, file, signal) => customInstance({
	url: "/api/mdm/internal/files/api/supporting-document/upload",
	method: "POST",
	headers: { "Content-Type": "application/octet-stream" },
	params,
	data: file,
	signal
});
const downloadSupportingDocumentFile = (id, signal) => customInstance({
	url: "/api/mdm/internal/files/api/supporting-document/download",
	method: "POST",
	headers: { "Content-Type": "application/json" },
	data: { id },
	responseType: "blob",
	signal
});
const uploadFinalDecisionDocumentFile = (params, file, signal) => customInstance({
	url: "/api/mdm/internal/files/api/final-decision-document/upload",
	method: "POST",
	headers: { "Content-Type": "application/octet-stream" },
	params,
	data: file,
	signal
});
const downloadFinalDecisionDocumentFile = (params, signal) => customInstance({
	url: "/api/mdm/internal/files/api/final-decision-document/download",
	method: "POST",
	headers: { "Content-Type": "application/json" },
	data: params,
	responseType: "blob",
	signal
});
const uploadTuPartFile = (params, file, signal) => customInstance({
	url: "/api/mdm/internal/files/api/tu-part/upload",
	method: "POST",
	headers: { "Content-Type": "application/octet-stream" },
	params,
	data: file,
	signal
});
const downloadTuPartFile = (params, signal) => customInstance({
	url: "/api/mdm/internal/files/api/tu-part/download",
	method: "POST",
	headers: { "Content-Type": "application/json" },
	data: params,
	responseType: "blob",
	signal
});
const getRegionalDepartments = (requestId, signal) => customInstance({
	url: `/api/mdm/internal/api/internal/requests/${requestId}/regional-departments`,
	method: "GET",
	signal
});
const putRegionalDepartments = (requestId, body, signal) => customInstance({
	url: `/api/mdm/internal/api/internal/requests/${requestId}/regional-departments`,
	method: "PUT",
	headers: { "Content-Type": "application/json" },
	data: body,
	signal
});
//#endregion
//#region src/runtime/filesystem-gge-extra.ts
const downloadDocumentDirect = (id, config) => customInstance({
	url: `/document/direct/download/${id}`,
	method: "GET",
	responseType: "blob",
	...config
});
const downloadDocumentById = (id, config) => customInstance({
	url: `/document/${id}`,
	method: "GET",
	responseType: "blob",
	...config
});
const downloadPdfDirect = (id, config) => customInstance({
	url: `/pdf/direct/download/${id}`,
	method: "GET",
	responseType: "blob",
	...config
});
const downloadPdfPages = (fileId, pages, config) => customInstance({
	url: `/pdf/${fileId}/pages`,
	method: "GET",
	params: { pages },
	responseType: "blob",
	...config
});
const getPdfPageImages = (fileId, params, config) => customInstance({
	url: `/pdf/${fileId}/page/images`,
	method: "GET",
	params,
	responseType: "blob",
	...config
});
const getPdfCropBox = (fileId, params, config) => customInstance({
	url: `/pdf/${fileId}/cropbox`,
	method: "GET",
	params,
	...config
});
const downloadPdfArea = (fileId, data, config) => customInstance({
	url: `/pdf/${fileId}/area/pdf`,
	method: "POST",
	data,
	responseType: "blob",
	...config
});
const downloadDocumentsZip = (ids, config) => customInstance({
	url: `/document/zip/download`,
	method: "POST",
	data: { ids },
	responseType: "blob",
	...config
});
/** POST /pdf/zip/download — ZIP выбранных PDF-файлов/папок (вкладка «Документация»). */
const downloadPdfZip = (ids, config) => customInstance({
	url: `/pdf/zip/download`,
	method: "POST",
	data: { ids },
	responseType: "blob",
	...config
});
const downloadPdfPagesZip = (data, config) => customInstance({
	url: `/pdf/zip/download/pages`,
	method: "POST",
	data,
	responseType: "blob",
	...config
});
//#endregion
//#region src/runtime/models-reconversion-extra.ts
/**
* POST /api/models/reconversion/model?modelId=… — реконвертировать свойства и геометрию модели.
* В спеке — `POST /reconversion/model`, gateway-prefix сервиса models добавляем вручную.
*/
const reconvertModel = (modelId, config) => customInstance({
	url: "/api/models/reconversion/model",
	method: "POST",
	params: { modelId },
	...config
});
//#endregion
//#region src/runtime/models-drawings-extra.ts
/**
* POST /api/models/drawing/zip/download — ZIP выбранных чертежей.
* Тело запроса — JSON-массив id чертежей.
*/
const downloadDrawingsZip = (drawingIds, config) => customInstance({
	url: "/api/models/drawing/zip/download",
	method: "POST",
	data: drawingIds,
	responseType: "blob",
	...config
});
//#endregion
export { ApiError, customInstance, downloadDocumentById, downloadDocumentDirect, downloadDocumentsZip, downloadDrawingsZip, downloadExternalFinalDecisionFile, downloadExternalRequestFile, downloadFinalDecisionDocumentFile, downloadInternalRequestFile, downloadPdfArea, downloadPdfDirect, downloadPdfPages, downloadPdfPagesZip, downloadPdfZip, downloadSupportingDocumentFile, downloadTuPartFile, errorResponseSchema, exportExternalOperatorRequestZip, exportExternalOperatorRequestsZip, exportInternalRequestsZip, exportInternalResultZip, getApiBaseUrl, getAuthToken, getPdfCropBox, getPdfPageImages, getRegionalDepartments, handleZodError, importInternalRequestArchive, logZodError, multiUploadExternalFiles, multiUploadInternalFiles, normalizeError, pageOf, parseApiResponse, putRegionalDepartments, reconvertModel, serializeParams, setApiBaseUrl, setAuthTokenGetter, softParse, uploadExternalRequestFile, uploadFinalDecisionDocumentFile, uploadInternalRequestFile, uploadSupportingDocumentFile, uploadTuPartFile };

//# sourceMappingURL=index.js.map